import axios from "axios";
import React, { useState, useEffect } from "react";
import { BASE_URL } from "../../../env";
import Sidebar from "../../../components/layout/Sidebar";
import { Form, Row, Col, Button, Card } from "react-bootstrap";
import Select from "react-select";
import CustomPagination from "../../../components/ui/CustomPagination";
import DataTable from "../../../components/ui/DataTable";
import { useAdminAuth } from "../../auth/AdminAuthProvider";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import { GiMoneyStack, GiReceiveMoney } from "react-icons/gi";
import { MdCurrencyExchange, MdExpandMore } from "react-icons/md";
import CountUp from "react-countup";
import "./RevenueProcessingFeeReport.css";
import StandardizedDateInput from "../../../components/ui/StandardizedDateInput";

const RevenueProcessingFeeReport = () => {
    const { adminUser } = useAdminAuth();
    const [reportData, setReportData] = useState([]);
    const [totals, setTotals] = useState({});
    const [fundingAgencyTotals, setFundingAgencyTotals] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fundingAgencies, setFundingAgencies] = useState([]);
    const [branches, setBranches] = useState([]);

    const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);
    const [currentPage, setCurrentPage] = useState(1);
    const [showMobileFilters, setShowMobileFilters] = useState(false);

    // Filters
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [selectedFundingAgency, setSelectedFundingAgency] = useState(null);
    const [selectedLoanType, setSelectedLoanType] = useState(null);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");

    const itemsPerPage = 20;

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const filterData = (data) => {
        let filtered = data;
        if (searchTerm && searchTerm.trim() !== "") {
            const term = searchTerm.trim().toLowerCase();
            filtered = filtered.filter((item) => {
                const memberName = String(item?.memberName || "").toLowerCase();
                const customerId = String(item?.customerId || "").toLowerCase();
                const loanId = String(item?.loanId || "").toLowerCase();
                const phoneNumber = String(item?.phoneNumber || "").toLowerCase();
                const aadharNo = String(item?.aadharNo || "").toLowerCase();
                const applicationId = String(item?.ApplicationId || "").toLowerCase();

                return (
                    memberName.includes(term) ||
                    customerId.includes(term) ||
                    loanId.includes(term) ||
                    phoneNumber.includes(term) ||
                    aadharNo.includes(term) ||
                    applicationId.includes(term)
                );
            });
        }
        return filtered;
    };

    const filteredData = filterData(reportData);
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const paginationData = filteredData.slice(indexOfFirstItem, indexOfLastItem);

    const OpenSidebar = () => {
        setOpenSidebarToggle(!openSidebarToggle);
    };

    // Loan Type Options
    const loanTypeOptions = [
        { value: "All", label: "All Loan Types" },
        { value: "Business Loan", label: "Business Loan" },
        { value: "JLG Loan", label: "JLG Loan" },
        { value: "GP Loan", label: "GP Loan" },
    ];

    // Fetch funding agencies
    useEffect(() => {
        const fetchFundingAgencies = async () => {
            try {
                const response = await axios.get(`${BASE_URL}/api/getModelData/funding_agencies`);
                if (response.data && response.data.data) {
                    const agencies = response.data.data.map((agency) => ({
                        value: agency.id,
                        label: agency.agencyName,
                    }));
                    setFundingAgencies([{ value: "All", label: "All Funding Agencies" }, ...agencies]);
                }
            } catch (error) {
                console.log("Error fetching funding agencies:", error);
            }
        };
        fetchFundingAgencies();
    }, []);

    // Fetch branches
    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const response = await axios.get(`${BASE_URL}/api/getall/branches`);
                if (response.data) {
                    const branchList = response.data.map((branch) => ({
                        value: branch.id,
                        label: branch.branchName,
                    }));
                    setBranches([{ value: "All", label: "All Branches" }, ...branchList]);
                }
            } catch (error) {
                console.log("Error fetching branches:", error);
            }
        };
        fetchBranches();
    }, []);

    // Fetch report data on filter change
    useEffect(() => {
        const fetchReportDataAuto = async () => {
            setLoading(true);
            try {
                const params = {
                    id: adminUser.user.id,
                    loanType: selectedLoanType?.value || "All",
                    fundingAgencyId: selectedFundingAgency?.value || "All",
                    branchId: selectedBranch?.value || "All",
                };

                if (fromDate && toDate) {
                    params.startDate = fromDate;
                    params.endDate = toDate;
                }

                const response = await axios.get(
                    `${BASE_URL}/api/getRevenueProcessingFeeReportData`,
                    { params }
                );

                if (response.data) {
                    setReportData(response.data.data || []);
                    setTotals(response.data.totals || {});
                    setFundingAgencyTotals(response.data.fundingAgencyTotals || []);
                } else {
                    console.log("Error in fetching Data");
                }
            } catch (error) {
                console.log("Error fetching Data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchReportDataAuto();
    }, [fromDate, toDate, selectedLoanType, selectedFundingAgency, selectedBranch, adminUser.user.id]);

    // Define columns with label and render logic
    const columns = [
        { label: "S No", render: (data, index) => indexOfFirstItem + index + 1 },
        { label: "Application ID", render: (data) => data.ApplicationId || "N/A" },
        { label: "Member Name", render: (data) => data.memberName || "N/A" },
        { label: "Loan Type", render: (data) => data.loanType || "N/A" },
        { label: "Funding Agency", render: (data) => data.fundingAgencyName || "N/A" },
        { label: "Branch", render: (data) => data.branchName || "N/A" },
        {
            label: "Loan Amount",
            render: (data) =>
                "₹" + Number(data.loanAmount || 0).toLocaleString("en-IN"),
        },
        {
            label: "Processing Fee (%)",
            render: (data) => Number(data.processingFeePercentage || 0).toFixed(2),
        },
        {
            label: "Processing Fee Amount",
            render: (data) =>
                "₹" + Number(data.processingFee || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
        },
        {
            label: "Revenue PF (%)",
            render: (data) => Number(data.revenueProcessingFeePercentage || 0).toFixed(2),
        },
        {
            label: "Revenue from PF",
            render: (data) =>
                "₹" + Number(data.revenueFromProcessingFee || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
        },
        {
            label: "GST (%)",
            render: (data) => Number(data.gstPercentage || 0).toFixed(2),
        },
        {
            label: "GST Amount",
            render: (data) =>
                "₹" + Number(data.gstOnRevenueProcessingFee || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
        },
        {
            label: "Net PF Revenue",
            render: (data) =>
                "₹" + Number(data.netRevenueFromProcessingFee || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
        },
        {
            label: "Disbursement Date",
            render: (data) =>
                data.disbursementDate
                    ? new Date(data.disbursementDate).toLocaleDateString("en-IN")
                    : "N/A",
        },
    ];

    return (
        <section className="master-layout">
            <div className="master-sidebar-wrapper">
                <Sidebar
                    openSidebarToggle={openSidebarToggle}
                    OpenSidebar={OpenSidebar}
                />
            </div>

            <div className="master-content-wrapper">
                <div className="master-header-wrapper">
                    <DashboardHeader OpenSidebar={OpenSidebar} />
                </div>

                <div className="master-card-container">
                    <div className="master-search-section">
                        <h1 className="TableHeaderText w-100 text-start">Revenue - Processing Fee Report</h1>

                        <button
                            className={`filter-toggle-btn ${showMobileFilters ? "active" : ""}`}
                            onClick={() => setShowMobileFilters(!showMobileFilters)}
                        >
                            <span style={{ fontWeight: 600, fontSize: "14px" }}>
                                {showMobileFilters ? "Hide Filters" : "Show Filters"}
                            </span>
                            <MdExpandMore size={22} />
                        </button>

                        <div className={`filter-container ${!showMobileFilters ? "mobile-hidden" : "mobile-visible"}`}>
                            <Row>
                                <Col xs={12} lg={4} md={6} className="mb-2">
                                    <Form.Group>
                                        <Form.Label>Search Customer</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Search Name, Customer ID, Loan Acc No, Phone..."
                                            value={searchTerm}
                                            onChange={(e) => {
                                                setSearchTerm(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={12} lg={2} md={6}>
                                    <Form.Group>
                                        <Form.Label>From Date</Form.Label>
                                        <StandardizedDateInput
                                            value={fromDate}
                                            onChange={(event) => {
                                                const val = event.target.value;
                                                setFromDate(val);
                                                if (val && toDate && selectedFundingAgency && selectedLoanType && selectedBranch) {
                                                    setTimeout(() => setShowMobileFilters(false), 300);
                                                }
                                            }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={12} lg={2} md={6}>
                                    <Form.Group>
                                        <Form.Label>To Date</Form.Label>
                                        <StandardizedDateInput
                                            value={toDate}
                                            onChange={(event) => {
                                                const val = event.target.value;
                                                setToDate(val);
                                                if (fromDate && val && selectedFundingAgency && selectedLoanType && selectedBranch) {
                                                    setTimeout(() => setShowMobileFilters(false), 300);
                                                }
                                            }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={12} lg={2} md={6}>
                                    <Form.Label>Funding Agency</Form.Label>
                                    <Select
                                        value={selectedFundingAgency}
                                        onChange={(val) => {
                                            setSelectedFundingAgency(val);
                                            if (fromDate && toDate && val && selectedLoanType && selectedBranch) {
                                                setTimeout(() => setShowMobileFilters(false), 300);
                                            }
                                        }}
                                        options={fundingAgencies}
                                        isClearable
                                        placeholder="Select Agency"
                                        className="filter-dropdown"
                                    />
                                </Col>
                                <Col xs={12} lg={3} md={6}>
                                    <Form.Label>Loan Type</Form.Label>
                                    <Select
                                        value={selectedLoanType}
                                        onChange={(val) => {
                                            setSelectedLoanType(val);
                                            if (fromDate && toDate && selectedFundingAgency && val && selectedBranch) {
                                                setTimeout(() => setShowMobileFilters(false), 300);
                                            }
                                        }}
                                        options={loanTypeOptions}
                                        isClearable
                                        placeholder="Select Type"
                                        className="filter-dropdown"
                                    />
                                </Col>
                                <Col xs={12} lg={3} md={6}>
                                    <Form.Label>Branch</Form.Label>
                                    <Select
                                        value={selectedBranch}
                                        onChange={(val) => {
                                            setSelectedBranch(val);
                                            if (fromDate && toDate && selectedFundingAgency && selectedLoanType && val) {
                                                setTimeout(() => setShowMobileFilters(false), 300);
                                            }
                                        }}
                                        options={branches}
                                        isClearable
                                        placeholder="Select Branch"
                                        className="filter-dropdown"
                                    />
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <div className="master-list-section">
                        <div className="master-table-wrapper" style={{ padding: "0 15px" }}>
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <DataTable
                                    columns={columns}
                                    data={paginationData}
                                    reportName="RevenueProcessingFeeReport"
                                    excelData={filteredData}
                                />
                            )}
                        </div>

                        <div className="master-pagination-wrapper">
                            <CustomPagination
                                totalPages={totalPages}
                                currentPage={currentPage}
                                paginate={paginate}
                totalItems={reportData.length}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default RevenueProcessingFeeReport;
